import React, { useState, FC, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  BenchmarksWrapper,
  LegendColorsMeaningCont,
  LegendColorsMeaningContRotated,
  LegendCont,
  LegendScoreBarCont,
  LegendScoreBarContRotated,
  LegendSide,
  LegendTextCustom,
  LinkCont,
  ResultLegendBar,
} from './ReportResultsLegend.styles';
import LegendScoreBar from '../LegendScoreBar/LegendScoreBar';
import Link from '../Link/Link';

interface IReportResultLegend {
  noBorder?: boolean;
  description?: string;
  leftPosition?: string;
  variant?: 'riskAnalysis' | 'recommendation' | 'basic' | 'simple' | 'rotated';
  legendExpanded?: boolean;
  expandLegend?: (status: boolean) => void;
  children?: React.ReactNode;
  hideExpand?: boolean;
}

const ReportResultsLegend: FC<IReportResultLegend> = ({
  noBorder,
  description,
  leftPosition,
  variant = 'basic',
  expandLegend,
  legendExpanded,
  hideExpand,
  children,
}) => {
  const { t } = useTranslation();
  const legendDescription = description || '';
  const [isExpanded, setIsExpanded] = useState(false);
  const expandStatus = legendExpanded || isExpanded;
  const expandMethod = expandLegend || setIsExpanded;
  const returnBasicLegend = useMemo(() => {
    return (
      <LegendColorsMeaningCont>
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="strong" />
        </LegendScoreBarCont>
        {t('report.results.strongScore')}
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="normal" />
        </LegendScoreBarCont>
        {t('report.results.normalScore')}
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="weak" />
        </LegendScoreBarCont>
        {t('report.results.weakScore')}
      </LegendColorsMeaningCont>
    );
  }, [t]);

  const returnBasicLegendWidthTogglers = useMemo(() => {
    return (
      <BenchmarksWrapper>
        <LegendColorsMeaningContRotated>
          <LegendScoreBarContRotated id="LegendScoreBarCont">
            <LegendScoreBar score="strong" />
          </LegendScoreBarContRotated>
          {t('report.results.strongScore')}
          <LegendScoreBarContRotated id="LegendScoreBarCont">
            <LegendScoreBar score="normal" />
          </LegendScoreBarContRotated>
          {t('report.results.normalScore')}
          <LegendScoreBarContRotated id="LegendScoreBarCont">
            <LegendScoreBar score="weak" />
          </LegendScoreBarContRotated>
          {t('report.results.weakScore')}
        </LegendColorsMeaningContRotated>
        {children}
      </BenchmarksWrapper>
    );
  }, [t, children]);

  const returnRiskAnalysisLegend = useMemo(() => {
    return (
      <LegendColorsMeaningCont>
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="high" />
        </LegendScoreBarCont>
        {t('report.riskAnalysis.highRisk')}
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="potential" />
        </LegendScoreBarCont>
        {t('report.riskAnalysis.potentialRisk')}
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="low" />
        </LegendScoreBarCont>
        {t('report.riskAnalysis.lowRisk')}
      </LegendColorsMeaningCont>
    );
  }, [t]);

  const returnRecommendationLegend = useMemo(() => {
    return (
      <LegendColorsMeaningCont>
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="should" />
        </LegendScoreBarCont>
        {t('report.recommendation.should')}
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="could" />
        </LegendScoreBarCont>
        {t('report.recommendation.could')}
        <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score="excel" />
        </LegendScoreBarCont>
        {t('report.recommendation.excel')}
        {/* <LegendScoreBarCont id="LegendScoreBarCont">
          <LegendScoreBar score='other'/>
        </LegendScoreBarCont>
        {t('report.recommendation.other')} */}
      </LegendColorsMeaningCont>
    );
  }, [t]);

  const returnLegendColors = useMemo(() => {
    switch (variant) {
      case 'riskAnalysis':
        return returnRiskAnalysisLegend;
      case 'recommendation':
        return returnRecommendationLegend;
      case 'basic':
        return returnBasicLegend;
      case 'rotated':
        return returnBasicLegendWidthTogglers;
    }
  }, [returnBasicLegend, returnRecommendationLegend, returnRiskAnalysisLegend, variant]);

  return (
    <ResultLegendBar id="ResultLegendBar" noBorder={noBorder} leftPosition={leftPosition}>
      {!_.isEmpty(legendDescription) && (
        <LegendSide hideExpand={hideExpand}>
          {hideExpand ? (
            <div dangerouslySetInnerHTML={{ __html: legendDescription || '' }} />
          ) : (
            <LegendTextCustom
              id="LegendText"
              isExpanded={expandStatus}
              isSimple={variant === 'simple'}
            >
              <div dangerouslySetInnerHTML={{ __html: legendDescription || '' }} />
            </LegendTextCustom>
          )}
        </LegendSide>
      )}
      {hideExpand ? null : (
        <LegendCont id="LegendCont" variant={variant}>
          <>
            {!_.isEmpty(legendDescription) && (
              <LinkCont>
                <Link
                  text={expandStatus ? t('report.results.showLess') : t('report.results.showMore')}
                  onClick={() => expandMethod(!expandStatus)}
                />
              </LinkCont>
            )}
            {variant !== 'simple' && returnLegendColors}
          </>
        </LegendCont>
      )}
    </ResultLegendBar>
  );
};

export default ReportResultsLegend;
